import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { usePostHog } from "posthog-js/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { ImageCategoryEnum, MemberThemeEnum, StatusStateEnum } from "~/types/enums";
import toastsActionCreators from "~/store/toasts/actions";
import { Grid, Button, Modal, Alert } from "~/components-shared";
import ProfileBasicInfoForm from "~/forms/ProfileBasicInfoForm";
import profileBasicInfoSchema from "~/forms/ProfileBasicInfoForm/profileBasicInfoSchema";
import authSelectors from "~/store/auth/selectors";
import authActionCreators from "~/store/auth/actions";
import { helpers } from "~/lib/helpers";
import ImageUploadControl from "../ImageUploadControl/ImageUploadControl";
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 100%;
`;
const ProfileBasicInfoUpdateModal = _ref => {
  let {
    onClose
  } = _ref;
  const {
    t
  } = useTranslation();
  const user = useSelector(authSelectors.getUser);
  const status = useSelector(authSelectors.getStatus);
  const error = useSelector(authSelectors.getError);
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const defaultValues = {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    pronouns: user?.pronouns || "",
    position: user?.position || "",
    profileImage: user?.profileImage || "",
    theme: user?.theme || MemberThemeEnum.DARK,
    department: user?.department || undefined,
    level: user?.level || undefined
  };
  const {
    handleSubmit,
    control,
    watch
  } = useForm({
    resolver: yupResolver(profileBasicInfoSchema),
    defaultValues
  });
  useEffect(() => {
    dispatch(authActionCreators.setTheme(watch("theme")));
  }, [watch("theme")]);
  const onSubmit = handleSubmit(data => {
    dispatch(authActionCreators.updateBasicInfoStarted(data, _ref2 => {
      let {
        data: newUser
      } = _ref2;
      if (newUser) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("ProfileBasicInfoUpdateModal.basicInfoUpdated"),
          timeout: 10000
        }));
        onClose();
      }
    }));
    posthog.capture(`Profile Basic Info – Save`, {
      ...helpers.objectDiff(defaultValues, data)
    });
  });
  const handleCancel = () => {
    dispatch(authActionCreators.actionChanged(undefined));
    onClose();
    posthog.capture(`Profile Basic Info – Cancel`);
  };
  return /*#__PURE__*/React.createElement(Modal.Body, {
    onClose: onClose,
    title: t("ProfileBasicInfoUpdateModal.updateBasicInformation"),
    maxWidth: "600px",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1fr 170px",
    gap: 5
  }, /*#__PURE__*/React.createElement(ProfileBasicInfoForm, {
    control: control
  }), /*#__PURE__*/React.createElement(ImageUploadControl, {
    initPreview: user?.profileImage,
    name: "profileImage",
    control: control,
    label: t("ProfileBasicInfoUpdateModal.uploadImage"),
    s: {
      mt: 8
    },
    category: ImageCategoryEnum.PROFILE,
    border: "circled",
    width: "170px",
    height: "170px"
  }))), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: handleCancel,
    rounded: true,
    display: "inline-block"
  }, t("common.actions.cancel")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    display: "inline-block",
    maxWidth: "230px",
    type: "submit",
    onClick: onSubmit,
    s: {
      ml: 2
    },
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("common.actions.save")))));
};
export default ProfileBasicInfoUpdateModal;